import Dexie, { type Table } from 'dexie';
import type { CanvasMeta, Id, LinkRecord, ObjectRecord, Transaction } from '@vp/shared-types';

/** Storage rows. `_tile` is an internal spatial bucket used for viewport-first loading (§11.5). */
export type ObjectRow = ObjectRecord & { _tile?: string };
export type LinkRow = LinkRecord;

export interface OplogRow {
  seq?: number;
  canvasId: Id;
  ts: number;
  tx: Transaction;
}

export interface AttachmentRow {
  hash: string;
  blob: Blob;
  mime: string;
  size: number;
  createdAt: number;
}

export interface QuarantineRow {
  id?: number;
  canvasId: Id;
  table: string;
  ts: number;
  reason: string;
  data: unknown;
}

export interface LogRow {
  id?: number;
  ts: number;
  level: 'debug' | 'info' | 'warn' | 'error';
  code: string;
  /** Ids, types, codes and timings only unless verbose debugging is enabled (§14.1). */
  data?: Record<string, unknown>;
  verbose?: boolean;
}

export interface SettingRow {
  key: string;
  value: unknown;
}

export interface ModuleRow {
  id: string;
  version: string;
  enabled: boolean;
  installedAt: number;
  /** Raw zip of the package. */
  archive: Blob;
  manifest: unknown;
  /** Granted permissions (subset of requested). */
  granted: string[];
  bundled?: boolean;
  /** Installed from the catalog with a checked signature (TZ 4). */
  official?: { keyId: string; signedAt: string } | null;
  isolated?: { at: number; reason: string } | null;
  /** API 1.6: values of the module's settings (manifest `contributes.settings`). */
  settings?: Record<string, unknown>;
}

/**
 * API 2.2: data a module keeps for itself (`platform.storage`). `scope` is a canvas id (the data
 * belongs to that canvas and travels in its archive) or `app` (the module's own, across canvases).
 */
export interface ModuleDataRow {
  moduleId: string;
  scope: string;
  key: string;
  value: unknown;
  /** Serialized size in characters, for the quota. */
  size: number;
  updatedAt: number;
}

export interface ModuleVersionRow {
  key: string; // `${id}@${version}`
  id: string;
  version: string;
  archive: Blob;
  manifest: unknown;
  savedAt: number;
}

export class VpDatabase extends Dexie {
  canvases!: Table<CanvasMeta, Id>;
  objects!: Table<ObjectRow, [Id, Id]>;
  links!: Table<LinkRow, [Id, Id]>;
  oplog!: Table<OplogRow, number>;
  attachments!: Table<AttachmentRow, string>;
  quarantine!: Table<QuarantineRow, number>;
  logs!: Table<LogRow, number>;
  settings!: Table<SettingRow, string>;
  modules!: Table<ModuleRow, string>;
  moduleVersions!: Table<ModuleVersionRow, string>;
  moduleData!: Table<ModuleDataRow, [string, string, string]>;

  constructor(name = 'vp', backend?: { indexedDB: IDBFactory; IDBKeyRange: typeof IDBKeyRange }) {
    super(name, backend ? { indexedDB: backend.indexedDB, IDBKeyRange: backend.IDBKeyRange } : undefined);
    this.version(1).stores({
      canvases: 'id, updatedAt',
      objects: '[canvasId+id], canvasId, [canvasId+_tile]',
      links: '[canvasId+id], canvasId',
      oplog: '++seq, canvasId, [canvasId+seq], ts',
      attachments: 'hash',
      quarantine: '++id, canvasId',
      logs: '++id, ts, level',
      settings: 'key',
      modules: 'id',
      moduleVersions: 'key, id',
    });
    // API 2.2: module storage
    this.version(2).stores({
      moduleData: '[moduleId+scope+key], [moduleId+scope], moduleId, scope',
    });
  }
}

export const TILE_SIZE = 4096;

export function tileKey(x: number, y: number): string {
  return Math.floor(x / TILE_SIZE) + ':' + Math.floor(y / TILE_SIZE);
}
